<template>
	<div>

		<cmps-loader v-if="loading"></cmps-loader>

    <header class="jumbo-page-header">
      <h1
        class="locator-title"
        id="aria-label_enrollment"
        tabindex="-1"
        style="outline: none"
      >
        Enrollment
      </h1>
      <p :style="{'font-size': 'small'}" class="locator-subtitle ml-4 pb-2">
        Mesa Public School offers online enrollment and re-enrollment via the Parent Portal
      </p>
    </header>

		<main role="main">

			<div class="container overview mb-5 px-3">
				<h3 class="font-family-montserrat is-size-3 pb-1 mb-3" style="border-bottom: 1px solid black;">Overview</h3>
				<p class="is-size-5">There are multiple enrollment processes in Mesa Public Schools that will depend on the school or program selected. A majority of schools will differentiate the enrollment process based on where the child lives in relation to the school. Every address within the boundaries of the Mesa Public School system is associated with a group of schools referred to as home schools. Home schools generally serve children living in the surrounding areas and neighborhoods, and use our traditional enrollment process <strong class="has-text-success">(1)</strong>. Children who want to attend a school other than their home schools will typically need to complete the open enrollment process <strong class="has-text-success">(2)</strong>. Some of our schools are considered choice schools and each have their own unique enrollment processes that are outlined on their websites <strong class="has-text-success">(3)</strong>. Our preschool program, KinderU, also has a unique enrollment process <strong class="has-text-success">(4)</strong>.</p>
			</div>

			<div class="container px-3">
				<div class="columns is-multiline is-desktop">

					<div class="column is-6-desktop p-0 pr-desktop-4 mb-5 mb-desktop-0">
						<h3 class="font-family-montserrat is-size-3 pb-1 mb-3" style="border-bottom: 1px solid black;">Options</h3>
						<ol class="options-list">
							<li :class="{ 'display-disabled': search.active && search.option !== 1, 'option-active': search.active && search.option === 1, 'is-size-5': true }">
								If you live within a schools boundaries, then please proceed to our <a href="http://www.mpsaz.org/enroll" target="_blank">online enrollment system</a>. If you would like to fill out <a href="http://www.mpsaz.org/parents/enrolling/forms" target="_blank">paper forms</a> you may print the appropriate forms, and bring them to the school’s office.
							</li>
							<li :class="{ 'display-disabled': search.active && search.option !== 2, 'option-active': search.active && search.option === 2, 'is-size-5': true }">
								If you live outside the schools boundaries, please download and complete the <a href="http://www.mpsaz.org/parents/open_enroll" target="_blank">open enrollment form.</a> Open enrollment is limited to availability and typically requires parents to manage transportation concerns.
							</li>
							<li :class="{ 'display-disabled': search.active && search.option !== 3, 'option-active': search.active && search.option === 3, 'is-size-5': true }">
								Select a choice school from the dropdown menu for a link to their enrollment instructions.
								<br>
								<div class="text-center my-4 choice-link" v-show="search.option === 3">
									<span><strong class="has-text-success">{{ search.choice_name }}</strong></span>
									<br>
									<a :href="search.choice_enrollment_url" target="_blank" v-text="search.choice_enrollment_url"></a>
								</div>
							</li>
							<li :class="{ 'display-disabled': search.active && search.option !== 4, 'option-active': search.active && search.option === 4, 'is-size-5': true }">If you are registering for preschool please proceed to our <a href="http://www.mpsaz.org/kinderu/registration" target="_blank">KinderU page.</a><a href="http://www.mpsaz.org/special_ed/prek_screen/" target="_blank"> Special education preschool screening and registration</a> information is also available.</li>
						</ol>
					</div>

					<div class="column d-flex flex-column is-6-desktop p-0 pl-desktop-4">
						<h3 class="font-family-montserrat is-size-3 pb-1 mb-3" style="border-bottom: 1px solid black;">Search</h3>
						<h6 class="is-size-5 pt-2">Select a school, and if prompted enter your address, to determine the enrollment process.</h6>
						<small><i>* If your city is not listed, then you live outside of the boundary area.</i></small>

	  				<div class="mt-4 mt-desktop-0 d-flex justify-content-center align-content-center flex-wrap flex-grow-1 animated fadeIn" v-if="search.active">
		  				<p class="has-text-white text-center p-3 w-100" style="background-color: #23d160; font-size: 1.75rem;" v-if="search.option === 1">
		  					<strong class="has-text-white">Address is in boundary</strong>
		  					<br>
		  					<span>{{ search.formatted_address }}</span>
		  					<br>
		  					<span class="has-text-white">(option 1)</span>
		  				</p>
		  				<p class="has-text-white text-center p-3 w-100" style="background-color: #ff3860; font-size: 1.75rem;" v-if="search.option === 2">
		  					<strong class="has-text-white">Address is out of boundary</strong>
		  					<br>
		  					<span>{{ search.formatted_address }}</span>
		  					<br>
		  					<span class="has-text-white">(option 2)</span>
		  				</p>
		  				<p class="text-center p-3 w-100" style="background-color: #209cee; font-size: 1.75rem;" v-if="search.option === 3">
		  					<strong class="has-text-white">Choice School</strong>
		  					<br>
		  					<strong class="has-text-white">{{ search.choice_name }}</strong>
		  					<br>
		  					<span class="has-text-white">(option 3)</span>
		  				</p>
		  				<p class="text-center p-3 w-100" style="background-color: #209cee; font-size: 1.75rem;" v-if="search.option === 4">
		  					<strong class="has-text-white">KinderU Program</strong>
		  					<br>
		  					<strong class="has-text-white">{{ search.preschool }}</strong>
		  					<br>
		  					<span class="has-text-white">(option 4)</span>
		  				</p>
							<button class="button mt-5" @click="search.active = false">Search Again</button>
		  			</div>

						<form class="p-tablet-3 animated fadeIn flex-grow-1" @submit.prevent="checkAddress" v-else>

							<div class="field">
								<label class="label">School</label>
								<div class="notranslate control is-expanded has-icons-left">
									<div class="select is-fullwidth">
										<select name="school" v-model="selectedSchool" ref="schoolSelect" required>
											<optgroup label="Preschool">
												<option v-bind:key="s.id" v-for="s in schools.preschool" :value="s">{{ s.name }}</option>
											</optgroup>
											<optgroup label="Elementary">
												<option v-bind:key="s.id" v-for="s in schools.elementary" :value="s">{{ s.name }}</option>
											</optgroup>
											<optgroup label="Junior High">
												<option v-bind:key="s.id" v-for="s in schools.junior_high" :value="s">{{ s.name }}</option>
											</optgroup>
											<optgroup label="High School">
												<option v-bind:key="s.id" v-for="s in schools.high_school" :value="s">{{ s.name }}</option>
											</optgroup>
											<optgroup label="Choice School">
												<option v-bind:key="s.id" v-for="s in schools.choice" :value="s">{{ s.name }}</option>
											</optgroup>
										</select>
									</div>
									<span class="icon is-small is-left">
							      <i class="fas fa-school"></i>
							    </span>
								</div>
							</div>

							<div class="columns">
								<div class="column is-8 field p-0 pr-tablet-1">

									<label class="label">Street</label>
									<div class="control">
										<input
											type="text" name="street" class="input"
											required
											v-model="street"
											ref="street"
											@keydown="errors.clear('msg')"
											:disabled="search.option === 3 || search.option === 4">
										<transition name="fade">
							  			<p class="has-text-danger pl-3 pt-1" v-text="errors.get('msg') || 'test'" :style="{ color: errors.empty ? 'transparent !important' : 'inherit' }"></p>
							  		</transition>
									</div>

								</div>
								<div class="column is-4 field p-0 pl-tablet-1">

									<label class="label">City *</label>
									<div class="control">
										<div class="select w-100">
											<select class="w-100" name="city" v-model="city" required :disabled="search.option === 3 || search.option === 4">
												<option value="" disabled>Select City</option>
												<option value="Mesa, AZ">Mesa, AZ</option>
		  									<option value="Chandler, AZ">Chandler, AZ</option>
		                    <option value="Tempe, AZ">Tempe, AZ</option>
		                    <option value="Scottsdale, AZ">Scottsdale, AZ</option>
		                    <option value="Apache Junction, AZ">Apache Junction, AZ</option>
											</select>
										</div>
									</div>

								</div>
							</div>
							<div class="field d-flex flex-row-reverse justify-content-between mt-3">
								<div class="control">
									<button type="submit" :class="{ 'button': true, 'is-info': true, 'is-loading': loading }" ref="search" :disabled="search.option === 3 || search.option === 4">Check Address</button>
								</div>
								<div class="control">
									<button class="button is-danger hover-cursor-pointer" @click.prevent="resetForm">Reset</button>
								</div>
							</div>
						</form>

					</div> <!-- end column -->

				</div> <!-- end columns -->
			</div> <!-- end container -->

		</main>

	</div>
</template>

<script>
	import { Errors } from '@/helpers/errors.js'
	import loader from '@/components/shared/cssLoader.vue'
	import throttle from 'lodash.throttle'

	export default {
		props: ['school_id'],
		components: {
			cmpsLoader: loader
		},
		data() {
			return {
				loading: false,
				street: '',
				city: '',
				selectedSchool: {},
				search: {
					active: false,
					option: 1, // 1: in boundary, 2: out of boundary - open enrollment, 3: choice school, 4: kinderU
					choice_name: '',
					choice_enrollment_url: '',
					preschool: '',
					formatted_address: ''
				},
				errors: new Errors()
			}
		},
		computed: {
			schools() { return this.$store.getters.categorizedSchools }
		},
		watch: {
			'selectedSchool': function(school) {
				this.loading = true

				// timeout is for ux, so user knows their selection is being recognized by the app
				setTimeout(() => {
					if (school.types.includes('choice')) {
						this.search.active = true
						this.search.option = 3
						this.search.choice_name = school.name
						switch (school.url_path) {
							case 'eagleridge':
								this.search.choice_enrollment_url = 'https://www.mdlp.org/k6online/reg.php?type=eagleridge'
								break
							case 'eva':
								this.search.choice_enrollment_url = 'https://www.mpsaz.org/eva'
								break
							case 'mdlp':
								this.search.choice_enrollment_url = 'https://www.mdlp.org/'
								break
							case 'academy':
								this.search.choice_enrollment_url = 'http://www.mpsaz.org/academy/parent_info/application_info/'
								break
							case 'franklin-alma':
							case 'franklin-brimhall':
							case 'franklin-east':
							case 'franklin-west':
								this.search.choice_enrollment_url = 'http://www.mpsaz.org/franklin/registration/'
								break
							case 'franklin-jr':
								this.search.choice_enrollment_url = 'http://www.mpsaz.org/franklinjr/application/'
		 						break
							case 'summitacademy':
								this.search.choice_enrollment_url = 'http://www.mpsaz.org/summitacademy/'
								break
						}

						// Analytics Tracking
						gtag('event', 'enroll_choice_school', { 'event_category': 'enroll_interaction', 'event_label': school.name + ' ' + school.end_name })

					} else if (school.id === 130 || school.id === 196) {
						this.search.active = true
						this.search.option = 4
						this.search.preschool = school.name + ' ' + school.end_name

						// Analytics Tracking
						gtag('event', 'enroll_kinderU', { 'event_category': 'enroll_interaction', 'event_label': school.name + ' ' + school.end_name })

					} else {
						this.search.active = false
						this.search.option = 1
						/*
							need to push this later in the stack
							toggling from option 3/4 (which causes input to be disabled) will not re-enable the input in time to focus street input
						*/
						setTimeout(() => {
							this.$refs.street.focus()
						})
					}
					this.loading = false
				}, 1000)
			}
		},
		methods: {
			checkAddress: throttle(function() {
				this.loading = true

				const streetRegex = /^[0-9]{1,6}\s{1,2}(NW|NE|SW|SE|north|south|west|east|n|e|s|w)(\.|,)?\s{1,2}[\w\.\s]+[a-z\.]$/i

				// validation checks
				if (this.street === '' || Object.keys(this.selectedSchool).length === 0) {
					this.loading = false
					return
				}

				if (!streetRegex.test(this.street)) {
					this.loading = false
					this.errors.record({ msg: ['Invalid address, please use traditional formatting'] })
					return
				}

				setTimeout(() => {
					this.$store.dispatch('inBoundary', { 'address': this.street + ' ' + this.city, 'polygons': this.selectedSchool.polygons })
						.then((results) => {
							this.search.active = true
							this.search.option = results.inBoundary ? 1 : 2
							this.search.formatted_address = results.address
							this.loading = false
							this.errors.clear('msg')

							// Analytics Tracking
							gtag('event', 'enroll_search', {
			          'event_category': 'enroll_interaction',
			          'event_label': this.selectedSchool.name + ' ' + this.selectedSchool.end_name + ': ' + this.street + ' ' + this.city
			        })
						})
						.catch((error) => {
							this.errors.record({ msg: [error] })
							this.loading = false
						})
				}, 1000)

			}, 1500),
			resetForm() {
				this.street = ''
				this.city = ''
				this.selectedSchool = {}
				this.search.active = false
			}
		},
		mounted() {
			// if user clicks here from enroll btn on card, select will choose corresponding school
			if (this.school_id) {
				this.$refs.schoolSelect._vOptions.forEach((school) => {
					if (school.id === this.school_id) {
						this.selectedSchool = school
					}
				})
			} else {
				document.querySelector('#aria-label_enrollment').focus()
			}
		},
	}
</script>

<style lang="scss" scoped>
	.display-disabled {
	  color: #c3c3c3;
	  text-decoration: line-through;
	  transform: scale(0.75);
	}
	.option-active {
		font-size: 1.25rem;
	}

	.container.overview p {
		line-height: 2;
	}
	.options-list {
    counter-reset: item;
    margin-left: .75rem;
	}
	.options-list li {
		display: block;
		padding: .5rem 0;
		transition: transform 0.5s;
	}
	.options-list li:before {
	  content: counter(item) ". ";
		counter-increment: item;
	  font-weight: bold;
	  color: hsl(141, 71%, 48%);
	}
	@media screen and (min-width: 769px) {

	}
  @media screen and (min-width: 1024px) {

	}
</style>
